import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { type SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
import { optional } from '@nullvoxpopuli/ember-composable-helpers';
import { and } from 'ember-truth-helpers';
import UiButtonGroup from 'garaje/components/ui/button-group';
import UiPill from 'garaje/components/ui-pill';
import type { FillColorString } from 'garaje/components/ui-pill';
import safeString from 'garaje/helpers/safe-string';

interface BlankSignature {
  Blocks: {
    default: [];
  };
}

const Blank = <template>{{yield}}</template> satisfies TOC<BlankSignature>;

interface SettingsPanelHeaderSignature {
  Args: {
    cancelText?: string;
    description?: string;
    iconClassList?: string;
    iconElement?: HTMLElement | SafeString;
    iconUri?: string;
    isOpen?: boolean;
    logoUri?: string;
    onCancel: () => void;
    pillFillColour?: FillColorString; // British spelling for legacy reasons
    pillText?: string;
    secondaryDescription?: string;
    showCancel?: boolean;
    showSecondaryDescription?: boolean;
    title?: string;
    twoTone?: boolean;
  };
  Blocks: {
    default: [
      {
        controls?: ComponentLike<BlankSignature>;
        icon?: ComponentLike<BlankSignature>;
        title?: ComponentLike<BlankSignature>;
        description?: ComponentLike<BlankSignature>;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class SpHeader extends Component<SettingsPanelHeaderSignature> {
  get showCancel(): boolean {
    return this.args.showCancel ?? true;
  }

  get pillFillColour(): FillColorString {
    return this.args.pillFillColour ?? 'grayDark';
  }

  <template>
    <div class="flex items-center bg-white {{if @twoTone 'p-4'}}" data-test-settings-panel-header ...attributes>
      {{#if @iconUri}}
        <div class="flex-none pr-4 w-[34px] h-[34px] mx-legacy-icon box-content {{@iconClassList}}">
          <img src={{@iconUri}} alt="" role="none" class="h-full w-full" />
        </div>
      {{else if @iconElement}}
        <div class="flex-none pr-4 w-[34px] h-[34px] mx-legacy-icon box-content {{@iconClassList}}">
          {{@iconElement}}
        </div>
      {{else if @logoUri}}
        <div class="flex-none pr-4 w-12.5 h-12.5 box-content">
          <img src={{@logoUri}} alt="" role="none" />
        </div>
      {{else}}
        {{yield (hash icon=(component Blank))}}
      {{/if}}
      <div class="flex-auto min-w-0 min-h-0 pr-4">
        <div class="flex items-center">
          <h4 data-test-settings-panel-header-title>
            {{#if @title}}
              {{@title}}
            {{else}}
              {{yield (hash title=(component Blank))}}
            {{/if}}
          </h4>
          {{#if @pillText}}
            <span class="ml-1 leading-none" data-test-settings-panel-header-pill>
              <UiPill @fill={{this.pillFillColour}}>
                {{@pillText}}
              </UiPill>
            </span>
          {{/if}}
        </div>
        <p class="m-0" data-test-settings-panel-header-desc>
          {{#if @description}}
            {{safeString @description}}
          {{else}}
            {{yield (hash description=(component Blank))}}
          {{/if}}
        </p>
        {{#if @showSecondaryDescription}}
          <p class="m-0 text-sm text-carbon-60" data-test-settings-panel-header-desc-secondary>
            {{safeString @secondaryDescription}}
          </p>
        {{/if}}
      </div>
      <UiButtonGroup @spacing="tight" class="flex-none">
        {{#if (and this.showCancel @isOpen)}}
          <button
            type="button"
            {{on "click" (optional @onCancel)}}
            class="btn btn--small btn--outline {{unless @cancelText 'btn--cancel'}}"
            data-test-cancel
          >
            {{#if @cancelText}}
              {{@cancelText}}
            {{else}}
              ×
            {{/if}}
          </button>
        {{/if}}
        {{yield (hash controls=(component Blank))}}
      </UiButtonGroup>
    </div>
  </template>
}
